import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/ui";
import { ArrowRight } from "lucide-react";

const DEMO = [
  ["Superadmin", "rajesh.sharma@smifs.com"],
  ["HR Admin", "neha.singh@smifs.com"],
  ["Manager", "priya.patel@smifs.com"],
  ["Employee", "amit.kumar@smifs.com"],
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user && user !== false) nav(user.role === "EMPLOYEE" ? "/me" : "/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) setError(r.error || "Invalid credentials");
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 lg:flex">
        <img src="https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 to-slate-900/95" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent font-head text-lg font-extrabold text-white">SM</div>
          <div>
            <div className="font-head text-xl font-extrabold tracking-tight text-white">SMIFS</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-400">Human Resources</div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-head text-4xl font-extrabold leading-tight tracking-tight text-white">The control room<br />for your people.</h2>
          <p className="mt-4 max-w-md text-slate-300">Hiring, payroll, performance, leave and engagement — one precise, enterprise-grade HR platform.</p>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} SMIFS Limited. All rights reserved.</div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent font-head text-lg font-extrabold text-white">SM</div>
          </div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">Welcome back</h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Sign in to your SMIFS HRMS account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="label">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="input" placeholder="you@smifs.com" />
            </div>
            <div>
              <label className="label">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="input" placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={loading} className="btn-accent w-full">
              {loading ? <Spinner className="text-white" /> : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Demo accounts · password ChangeMe123!</p>
            <div className="grid grid-cols-2 gap-1.5">
              {DEMO.map(([role, em]) => (
                <button key={em} onClick={() => { setEmail(em); setPassword("ChangeMe123!"); }}
                  data-testid={`demo-${role.toLowerCase().replace(" ", "-")}`}
                  className="rounded-md border border-slate-200 px-2 py-1.5 text-left text-xs hover:border-accent hover:bg-accent/5 dark:border-slate-700">
                  <span className="block font-semibold text-slate-700 dark:text-slate-200">{role}</span>
                  <span className="block truncate text-[10px] text-slate-400">{em}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
